/**
 * googleDriveOAuth — the browser side of the OAuth callback (P0).
 *
 * Google redirects the browser to GOOGLE_OAUTH_REDIRECT_URI, which is the vibe
 * SPA path `/modules/google-drive/callback?code&state`. Because Google gives no
 * Bearer on that redirect, the flow keeps the JWT-authenticated `exchange` call
 * in the module: the connect step opens Google consent in a POPUP; this shim,
 * run once at SPA boot (main.tsx), detects the callback URL and delivers
 * {code, state} back to the opener (the still-authenticated parent tab), which
 * POSTs /exchange. If the popup was blocked (full-page redirect, no opener) it
 * stashes the pair and returns to the app, where the module consumes it.
 *
 * This adds NO public backend path (design doc §D4) — the exchange is always a
 * Bearer call from an authenticated tab.
 */
export const GD_CALLBACK_PATH = '/modules/google-drive/callback';
export const GD_OAUTH_MESSAGE = 'gdrive-oauth';
export const GD_PENDING_KEY = 'gdrive_oauth_pending';

/**
 * Called at SPA boot BEFORE rendering. Returns true if the current URL is the
 * OAuth callback and it was handled (the caller must then NOT render the app —
 * the popup closes, or a full-page redirect navigates away).
 */
export function handleOAuthCallbackAtBoot(): boolean {
  if (window.location.pathname !== GD_CALLBACK_PATH) return false;
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  if (!code || !state) return false;

  if (window.opener) {
    // Popup path: hand the pair to the authenticated parent tab and close.
    window.opener.postMessage(
      { source: GD_OAUTH_MESSAGE, code, state },
      window.location.origin,
    );
    window.close();
    return true;
  }

  // Popup-blocked / full-page redirect: stash and return to the app; the
  // module consumes it on mount via takePendingOAuthCallback().
  try {
    sessionStorage.setItem(GD_PENDING_KEY, JSON.stringify({ code, state }));
  } catch {
    /* private mode — the pair is lost; the user retries Connect (benign) */
  }
  window.location.replace('/');
  return true;
}

/** Module-side: pop a stashed full-redirect callback (single-use), or null. */
export function takePendingOAuthCallback(): { code: string; state: string } | null {
  try {
    const raw = sessionStorage.getItem(GD_PENDING_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(GD_PENDING_KEY);
    const p = JSON.parse(raw);
    if (p && typeof p.code === 'string' && typeof p.state === 'string') {
      return { code: p.code, state: p.state };
    }
  } catch {
    /* ignore */
  }
  return null;
}
